import React, { useEffect } from "react";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ReactComponent as TimeSave } from "assets/svg/measurement-based-care/time-save.svg";
import { ReactComponent as AutoGenerated } from "assets/svg/measurement-based-care/auto-generated.svg";
import { ReactComponent as DataVisualization } from "assets/svg/measurement-based-care/data-viz.svg";
import { ReactComponent as DigestibleResults } from "assets/svg/measurement-based-care/digestible-results.svg";
import { ReactComponent as FullEHR } from "assets/svg/measurement-based-care/full-ehr.svg";
import { ReactComponent as PatientProgress } from "assets/svg/measurement-based-care/patient-progress.svg";
import LazyImage from "components/lazy-image";
import CustomAccordion from "components/custom-accordion/CustomAccordion";
import MBCPhoneView from "./MBCPhoneView";
import Footer from "components/footer/Footer";
import FullWidthImage from "components/full-width-image";

import { useTimeOnPageContext } from "contexts/time-on-page";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";

import { Helmet } from "react-helmet";

import { AmplitudePageName /*, track */ } from "utils/amplitude";

import getClasses from "utils/getClasses";
import styles from "./MeasurementBasedCare.module.scss";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "measurement-based-care-page";

const STEP_1 = "/img/measurement-based-care/step-1.png";
const STEP_2 = "/img/measurement-based-care/step-2.png";
const STEP_3 = "/img/measurement-based-care/step-3.png";
const STEP_4 = "/img/measurement-based-care/step-4.png";
const STEP_5 = "/img/measurement-based-care/step-5.png";
const CARD_1 = "/img/measurement-based-care/card-1.png";
const CARD_2 = "/img/measurement-based-care/card-2.png";
const CARD_3 = "/img/measurement-based-care/card-3.png";
const CARD_4 = "/img/measurement-based-care/card-4.png";
const LANDING = "/img/measurement-based-care/landing.png";

export const reasons = [
  {
    icon: <TimeSave />,
    title: "Time-Saving Automations",
  },
  {
    icon: <FullEHR />,
    title: "Full EHR-<br />Integration",
  },
  {
    icon: <DataVisualization />,
    title: "Data <br /> Visualization",
  },
  {
    icon: <PatientProgress />,
    title: "Improved <br /> Patient Progress",
  },
  {
    icon: <AutoGenerated />,
    title: "Auto-Generated Assessments",
  },
  {
    icon: <DigestibleResults />,
    title: "Digestible  <br />Results",
  },
];

export const testimonials = [
  {
    id: "0",
    content:
      "Automating PHQ-9 has been a game-changer for my practice, saving me valuable time and allowing for more focused patient interactions. I used to do this all in excel!",
    date: "February 3, 2023",
    author: "Verified Orchid Clinician",
  },
  {
    id: "1",
    content:
      "Since our group practice adopted auto assessments, we've seen data-driven improvements in client outcomes. Our clients also see progress.",
    date: "April 20, 2023",
    author: "Verified Orchid Clinician",
  },
  {
    id: "2",
    content:
      "In school I learned that measurement-based care is the gold standard. In reality, sending and tracking assessments is hard to manage! With Orchid, my assessments have been automated and I'm all the better for it.",
    date: "December 8, 2023",
    author: "Verified Orchid Clinician",
  },
];

export const questions = [
  {
    id: "0",
    question: "What is Orchid?",
    answer: `<p>Orchid is an innovative electronic health record built by clinicians for clinicians. We’re proud to be a privately
      owned company, <a target="_blank" href="https://www.ycombinator.com/companies/orchid"><span
              style="color:rgb(17, 85, 204);">backed by Y Combinator</span></a>.&nbsp;</p>
  <p>Not only is Orchid’s EHR the most affordable private practice management software on the market. We also lead the way
      with cutting-edge features like AI-driven insights, measurement-based mental health care, and robust automations
      that make client no-shows ancient history.&nbsp;</p>
  <p>Founded by mental health professionals, Orchid makes it a priority to anticipate and evolve with your practice’s
      needs — making us the perfect partner for private practice clinicians seeking efficiency, reliability, and
      cost-effectiveness.</p>
  <p><a target="_blank" href="https://orchid.exchange/about-us"><span style="color:rgb(17, 85, 204);">Read more about
              Orchid’s mission →</span></a></p>`,
  },
  {
    id: "1",
    question: "How do I get access to Orchid’s MBC tools?",
    answer:
      "All of Orchid’s measurement-based care tools are included in any of our subscription plans.",
  },
  {
    id: "2",
    question: "Can I customize my assessment forms with Orchid?",
    answer:
      "Absolutely. Orchid’s EHR features the most customizable template builder out there, with a user-friendly drag-and-drop editor. That level of customization equips you with the tools to tailor all of your assessment forms, progress note templates, intake forms, and more to suit the specific goals and needs of your practice.",
  },
  {
    id: "3",
    question: "How do I sign up for Orchid?",
    answer: `<p>The easiest way to sign up for Orchid is to get free 30-day access to our Core subscription plan. At any point during
      that 30-day free trial, you’ll be able to choose the right paid plan for you.&nbsp;</p>
  <p><a target="_blank" href="https://orchid.exchange/pricing"><span style="color:rgb(17, 85, 204);">Get your free trial
              →&nbsp;</span></a></p>`,
  },
];

const MeasurementBasedCare = () => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const { isPhonePortrait } = usePhonePortraitContext();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Orchid EHR | Measurement-Based Care Tools For Your Practice
        </title>
        <meta
          name="description"
          content="Optimize your practice with Orchid’s time-saving and life-saving measurement-based care tools."
        />
        <meta
          property="og:title"
          content="Orchid EHR | Measurement-Based Care Tools For Your Practice"
        />
        <meta
          property="og:description"
          content="Optimize your practice with Orchid’s time-saving and life-saving measurement-based care tools."
        />

        <meta
          name="twitter:title"
          content="Orchid EHR | Measurement-Based Care Tools For Your Practice"
        />
        <meta
          name="twitter:description"
          content="Optimize your practice with Orchid’s time-saving and life-saving measurement-based care tools."
        />
      </Helmet>
      {isPhonePortrait ? (
        <MBCPhoneView />
      ) : (
        <div>
          <div className={styles["empower"]}>
            <Container className={getClasses("d-flex", styles["container"])}>
              <div className={styles["empower__left"]}>
                <div className={styles["empower__title"]}>
                  Empower Your Clients with Measurement-Based Care
                </div>
                <div
                  className={getClasses(
                    styles["bottom-36"],
                    styles["empower__text"],
                  )}
                >
                  Save more hours (and more patients) with EHR-integrated
                  measurement-based care tools for your private practice.
                </div>
                <div className={"d-flex"}>
                  <Button
                    size={"lg"}
                    className={styles["action"]}
                    as={"a"}
                    target={"_blank"}
                    rel={"noopener noreferrer"}
                    href={"https://calendly.com/hey-orchid/orchid-walkthrough"}
                  >
                    Book Your Free Demo
                  </Button>
                </div>
              </div>
              <div className={styles["empower__right"]}>
                <LazyImage src={LANDING} width={672} height={568} fluid />
              </div>
            </Container>
          </div>

          <Container className={styles["container"]}>
            <h1 className={styles["title"]}>
              Time-Saving Automations for You, <br />
              Life-Saving Results for Patients
            </h1>
            <div className={styles["start-trial"]}>
              Streamline your workflow using Orchid’s measurement-based care
              (MBC) automations.
            </div>

            <Row className={styles["step-row"]}>
              <Col className={styles["step-left"]}>
                <FullWidthImage src={CARD_1} fluid />
              </Col>
              <Col className={styles["step-right"]}>
                <div className={styles["step-title"]}>
                  Improve patient health outcomes
                </div>
                <div className={styles["step-content"]}>
                  With Orchid’s MBC tools, <b>83% of patients</b> who regularly
                  took PHQ-9 assessments showed a marked{" "}
                  <b>improvement in symptoms</b> of depression.
                </div>
              </Col>
            </Row>

            <Row className={styles["step-row"]}>
              <Col className={styles["step-2-left"]}>
                <div className={styles["step-title"]}>
                  Cut patient no-shows by up to 25%
                </div>
                <div className={styles["step-content"]}>
                  <b>Save more than $1,000 annually</b> just by decreasing
                  patient cancellations and no-shows, while increasing patient
                  engagement, thanks to Orchid’s{" "}
                  <b>automated measurement-based care tools</b>. 
                </div>
              </Col>
              <Col className={styles["step-2-right"]}>
                <FullWidthImage src={CARD_2} fluid />
              </Col>
            </Row>

            <Row className={styles["step-row"]}>
              <Col className={styles["step-left"]}>
                <FullWidthImage src={CARD_3} fluid />
              </Col>
              <Col className={styles["step-right"]}>
                <div className={styles["step-title"]}>
                  Reduce clinician turnover by up to 75%
                </div>
                <div className={styles["step-content"]}>
                  Get <b>the antidote to burnout and high turnover</b> by
                  equipping your practice with Orchid’s automated MBC tools that{" "}
                  <b>lessen the administrative overload</b>, saving you tens of
                  thousands in lost revenue and hiring costs.
                </div>
              </Col>
            </Row>

            <Row className={styles["step-row"]}>
              <Col className={styles["step-2-left"]}>
                <div className={styles["step-title"]}>Earn more revenue</div>
                <div className={styles["step-content"]}>
                  Did you know that insurers may give you{" "}
                  <b>higher reimbursement rates</b> if you can show data-driven
                  outcomes? <b>Earn up to 10% more revenue per appointment</b>{" "}
                  by using Orchid’s measurement-based tools. 
                </div>
              </Col>
              <Col className={styles["step-2-right"]}>
                <FullWidthImage src={CARD_4} fluid />
              </Col>
            </Row>
            <div className={"d-flex justify-content-center"}>
              <Button size={"lg"} as={Link} to="" className={styles["action"]}>
                Save More with MBC
              </Button>
            </div>
          </Container>
          <div className={styles["why-automate"]}>
            <Container className={styles["why-automate__container"]}>
              <h2 className={styles["why-automate__title"]}>
                Why automate measurement-based care with Orchid?
              </h2>
              <Row>
                {reasons.map((reason) => {
                  return (
                    <Col className={styles["why-automate__col"]} md={4}>
                      <Card className={styles["why-automate__card"]}>
                        <div>{reason.icon}</div>
                        <div
                          className={styles["why-automate__card-text"]}
                          dangerouslySetInnerHTML={{ __html: reason.title }}
                        />
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Container>
          </div>
          <Container className={styles["container"]}>
            <h1 className={styles["title"]}>
              How do Orchid’s measurement-based care tools work?
            </h1>
            <div className={styles["start-trial"]}>
              Automate every step of MBC — all within the same EHR solution that
              helps you manage the rest of your practice.
            </div>
            <Row className={styles["step-row"]}>
              <Col className={styles["step-left"]}>
                <FullWidthImage src={STEP_1} fluid />
              </Col>
              <Col className={styles["step-right"]}>
                <div className={styles["step-tag"]}>STEP 1</div>
                <div className={styles["step-title"]}>
                  Schedule your assessment forms.
                </div>
                <div className={styles["step-content"]}>
                  Select PHQ-9, GAD-7, or PCL-5 baseline forms from our
                  pre-generated template library, and set them to auto-send
                  before appointments.
                </div>
              </Col>
            </Row>
            <Row className={styles["step-row"]}>
              <Col className={styles["step-left"]}>
                <FullWidthImage src={STEP_2} fluid />
              </Col>
              <Col className={styles["step-right"]}>
                <div className={styles["step-tag"]}>STEP 2</div>
                <div className={styles["step-title"]}>Automate reminders.</div>
                <div className={styles["step-content"]}>
                  Achieve a 97% form completion rate with Orchid’s automatic
                  reminders to patients. 
                </div>
              </Col>
            </Row>
            <Row className={styles["step-row"]}>
              <Col className={styles["step-left"]}>
                <FullWidthImage src={STEP_3} fluid />
              </Col>
              <Col className={styles["step-right"]}>
                <div className={styles["step-tag"]}>STEP 3</div>
                <div className={styles["step-title"]}>
                  See new insights into patient health.
                </div>
                <div className={styles["step-content"]}>
                  Orchid analyzes those forms within seconds and visualizes the
                  results for you.
                </div>
              </Col>
            </Row>
            <Row className={styles["step-row"]}>
              <Col className={styles["step-left"]}>
                <FullWidthImage src={STEP_4} fluid />
              </Col>
              <Col className={styles["step-right"]}>
                <div className={styles["step-tag"]}>STEP 4</div>
                <div className={styles["step-title"]}>
                  Get instant alerts about high-risk individuals.
                </div>
                <div className={styles["step-content"]}>
                  We’ll sound the alarms immediately to flag any critical
                  patient responses, so you can promptly intervene as needed. 
                </div>
              </Col>
            </Row>
            <Row className={styles["step-row"]}>
              <Col className={styles["step-left"]}>
                <FullWidthImage src={STEP_5} fluid />
              </Col>
              <Col className={styles["step-right"]}>
                <div className={styles["step-tag"]}>STEP 5</div>
                <div className={styles["step-title"]}>
                  Help patients more effectively.
                </div>
                <div className={styles["step-content"]}>
                  When you use Orchid’s measurement-based care tools to inform
                  your treatment plans, patients are 3x more likely to reach
                  remission in their mental health concerns. 
                </div>
              </Col>
            </Row>
            <div className={"d-flex justify-content-center"}>
              <Button size={"lg"} as={Link} to="" className={styles["action"]}>
                Get Results with Orchid’s MBC
              </Button>
            </div>
          </Container>
          <div className={styles["i-love-ehr"]}>
            <Container className={styles["i-love-ehr__container"]}>
              <div
                className={getClasses(
                  styles["bottom-56"],
                  styles["i-love-ehr__title"],
                )}
              >
                Clinicians like you are growing with Orchid
              </div>
              <Row className={styles["item-list"]}>
                {testimonials.map((item) => (
                  <Col md={4} key={item.id} className={styles["item"]}>
                    <Card className={styles["card"]}>
                      <div className={styles["card__body"]}>
                        <div className={styles["quote-sign"]}>“</div>
                        <div>{item.content}</div>
                      </div>
                      <div className={styles["card__footer"]}>
                        <span>{item.date}</span>
                        <span>•</span>
                        <span>{item.author}</span>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Container>
          </div>
          <div className={styles["get-power"]}>
            <Container className={styles["get-power__container"]}>
              <div
                className={getClasses(
                  styles["bottom-56"],
                  styles["get-power__title"],
                )}
              >
                Get the power of Orchid today.
              </div>
              <div className={styles["bottom-36"]}>
                Transform your practice with increased efficiency, deeper
                insights, and improved client outcomes.
              </div>
              <div>
                <Button
                  size={"lg"}
                  className={styles["action"]}
                  as={Link}
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                  to={"/pricing"}
                >
                  Start Your Free Trial
                </Button>
              </div>
            </Container>
          </div>
          <Container className={styles["faq"]}>
            <h1 className={getClasses(styles["bottom-56"], styles["title"])}>
              Questions? We’ve got answers.
            </h1>
            {questions.map(({ id, question, answer }, idx) => {
              return (
                <CustomAccordion
                  key={id}
                  id={id}
                  index={idx}
                  question={question}
                  answer={answer}
                  amplitudePageName={AMPLITUDE_PAGE_NAME}
                />
              );
            })}

            <div className={styles["more-question"]}>
              <div className={styles["more-question__title"]}>
                Got more questions about Orchid?
              </div>
              <Button as={Link} to={"/orchid-ehr-faq"}>
                Read the rest of our FAQs
              </Button>
            </div>
            <div className={styles["slogan"]}>
              <div className={styles["slogan__title"]}>
                Private Practice – Made Perfect
              </div>
              <div className={styles["slogan__content"]}>
                Save time, money, and admin headaches when you switch to Orchid
                today.
                <br />
                Experience the difference of managing your practice with Orchid.
              </div>
              <div className={"d-flex justify-content-center"}>
                <Button
                  className={styles["action"]}
                  as={Link}
                  to={"/professional-register"}
                >
                  Switch to Orchid
                </Button>
              </div>
            </div>
          </Container>

          <Footer AMPLITUDE_PAGE_NAME={AMPLITUDE_PAGE_NAME} />
        </div>
      )}
    </>
  );
};

export default MeasurementBasedCare;
